import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
	AlertCircle,
	ArrowLeft,
	Check,
	CheckCircle2,
	ChevronRight,
	Download,
	FileSpreadsheet,
	Loader2,
	Upload,
	Users,
	X,
} from "lucide-react";
import { useCallback, useState } from "react";
import { PermissionDeniedPage } from "@/components/team/PermissionDeniedPage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { useRole } from "@/hooks/use-role";
import { useSession } from "@/hooks/use-session";
import { trackCsvImported } from "@/lib/analytics-events";
import { apiService } from "@/lib/api-service";
import type {
	CSVImportJob,
	CSVUploadResponse,
	DetectedColumn,
} from "@/types/contacts";

export const Route = createFileRoute("/_app/_/contacts/import")({
	component: CSVImportWizardPage,
	validateSearch: (search: Record<string, unknown>): { from?: string } => ({
		from: (search.from as string) || undefined,
	}),
});

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CONTACT_FIELDS = [
	{ value: "first_name", label: "First Name" },
	{ value: "last_name", label: "Last Name" },
	{ value: "email", label: "Email" },
	{ value: "phone", label: "Phone" },
	{ value: "address_line1", label: "Address" },
	{ value: "address_line2", label: "Address Line 2" },
	{ value: "city", label: "City" },
	{ value: "state", label: "State" },
	{ value: "zip", label: "Zip Code" },
	{ value: "source", label: "Source" },
] as const;

const DONOR_FIELDS = [
	{ value: "first_name", label: "First Name" },
	{ value: "last_name", label: "Last Name" },
	{ value: "email", label: "Email" },
	{ value: "amount", label: "Donation Amount" },
	{ value: "donated_at", label: "Donation Date" },
	{ value: "phone", label: "Phone" },
	{ value: "address_line1", label: "Address" },
	{ value: "address_line2", label: "Address Line 2" },
	{ value: "city", label: "City" },
	{ value: "state", label: "State" },
	{ value: "zip", label: "Zip Code" },
	{ value: "employer", label: "Employer" },
	{ value: "occupation", label: "Occupation" },
	{ value: "source", label: "Source" },
] as const;

const REQUIRED_FIELDS = ["first_name", "last_name"];
const DONOR_REQUIRED_FIELDS = ["first_name", "last_name", "email", "amount"];

type WizardStep = "upload" | "mapping" | "progress" | "results";

// ---------------------------------------------------------------------------
// Upload Step
// ---------------------------------------------------------------------------

function UploadStep({
	onUploadComplete,
	campaignId,
	isDonorImport,
}: {
	onUploadComplete: (data: CSVUploadResponse) => void;
	campaignId: number;
	isDonorImport?: boolean;
}) {
	const [isDragOver, setIsDragOver] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const uploadMutation = useMutation({
		mutationFn: (file: File) =>
			isDonorImport
				? apiService.uploadDonorCSV(campaignId, file)
				: apiService.uploadCSV(campaignId, file),
		onSuccess: (data) => onUploadComplete(data),
		onError: (err: Error) => setError(err.message),
	});

	const handleFile = useCallback(
		(file: File) => {
			setError(null);
			const validTypes = [
				"text/csv",
				"text/tab-separated-values",
				"application/vnd.ms-excel",
				"application/octet-stream",
			];
			const isCSV = file.name.endsWith(".csv") || file.name.endsWith(".tsv");
			if (!validTypes.includes(file.type) && !isCSV) {
				setError("Please upload a CSV or TSV file.");
				return;
			}
			// 10MB limit
			if (file.size > 10 * 1024 * 1024) {
				setError(
					"File exceeds 10 MB. For larger files, please reach out to support.",
				);
				return;
			}
			uploadMutation.mutate(file);
		},
		[uploadMutation],
	);

	const handleDrop = useCallback(
		(e: React.DragEvent) => {
			e.preventDefault();
			setIsDragOver(false);
			const file = e.dataTransfer.files[0];
			if (file) handleFile(file);
		},
		[handleFile],
	);

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					<Upload className="size-5" />
					Upload CSV File
				</CardTitle>
			</CardHeader>
			<CardContent>
				<div
					className={`flex flex-col items-center justify-center rounded-lg border-2 border-dashed p-12 transition-colors ${
						isDragOver
							? "border-primary bg-primary/5"
							: "border-muted-foreground/25 hover:border-muted-foreground/50"
					}`}
					onDragOver={(e) => {
						e.preventDefault();
						setIsDragOver(true);
					}}
					onDragLeave={() => setIsDragOver(false)}
					onDrop={handleDrop}
				>
					{uploadMutation.isPending ? (
						<>
							<Loader2 className="mb-4 size-10 animate-spin text-primary" />
							<p className="text-sm font-medium">Uploading and analyzing...</p>
						</>
					) : (
						<>
							<FileSpreadsheet className="mb-4 size-10 text-muted-foreground/50" />
							<p className="text-sm font-medium">
								Drag and drop your CSV file here
							</p>
							<p className="mt-1 text-xs text-muted-foreground">
								or click to browse. Supports CSV and TSV files up to 10 MB.
							</p>
							<label className="mt-4">
								<input
									type="file"
									accept=".csv,.tsv,text/csv,text/tab-separated-values"
									className="hidden"
									onChange={(e) => {
										const file = e.target.files?.[0];
										if (file) handleFile(file);
									}}
								/>
								<Button variant="outline" asChild className="cursor-pointer">
									<span>Choose File</span>
								</Button>
							</label>
							<a
								href="/sample-contacts-import.csv"
								download
								className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
							>
								<Download className="size-3" />
								Download sample CSV template
							</a>
						</>
					)}
				</div>

				{error && (
					<div className="mt-4 flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
						<AlertCircle className="size-4 shrink-0" />
						{error}
					</div>
				)}
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Mapping Step
// ---------------------------------------------------------------------------

function confidenceBadge(confidence: number) {
	if (confidence >= 0.9)
		return (
			<Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
				High
			</Badge>
		);
	if (confidence >= 0.6)
		return (
			<Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300">
				Medium
			</Badge>
		);
	if (confidence > 0)
		return (
			<Badge className="bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300">
				Low
			</Badge>
		);
	return <Badge variant="outline">Unmapped</Badge>;
}

function MappingStep({
	uploadData,
	onConfirm,
	campaignId,
	isDonorImport,
	onModeDetected,
}: {
	uploadData: CSVUploadResponse;
	onConfirm: (jobId: number) => void;
	campaignId: number;
	isDonorImport?: boolean;
	onModeDetected?: (hasDonationAmounts: boolean) => void;
}) {
	// Detect if any column is mapped to "amount" (checked dynamically as user changes mappings)
	const [columns, setColumns] = useState<DetectedColumn[]>(
		uploadData.detected_columns,
	);

	// When from donors tab, check ACTUAL mapping (not just header keywords) to decide pipeline
	const amountIsMapped = isDonorImport
		? columns.some((col) => col.mapped_to === "amount")
		: false;

	// When from donors tab: use donor import if amount is mapped, otherwise prospect import
	const useDonorPipeline = isDonorImport && amountIsMapped;
	const isProspectImport = isDonorImport && !amountIsMapped;

	// Always show donor fields when from donors tab so user CAN map amount
	const fields = isDonorImport ? DONOR_FIELDS : CONTACT_FIELDS;
	const requiredFields = useDonorPipeline
		? DONOR_REQUIRED_FIELDS
		: REQUIRED_FIELDS;

	const [errors, setErrors] = useState<string[]>([]);

	const updateMapping = (index: number, value: string | null) => {
		setColumns((prev) =>
			prev.map((col, i) =>
				i === index
					? {
							...col,
							mapped_to: value === "__skip" ? null : value,
							confidence: value === "__skip" ? 0 : col.confidence,
						}
					: col,
			),
		);
		setErrors([]);
	};

	// Check required fields
	const mappedTargets = columns
		.map((c) => c.mapped_to)
		.filter(Boolean) as string[];
	const missingRequired = requiredFields.filter(
		(f) => !mappedTargets.includes(f),
	);

	const confirmMutation = useMutation({
		mutationFn: async () => {
			const mappings: Record<string, string | null> = {};
			for (const col of columns) {
				mappings[col.csv_header] = col.mapped_to;
			}

			if (useDonorPipeline) {
				// Amount column detected → full donor import (Donor + CampaignDonation)
				const result = await apiService.confirmDonorMapping(
					campaignId,
					uploadData.job_id,
					mappings,
				);
				if (!result.valid) {
					throw new Error(result.errors.join("; "));
				}
				await apiService.startDonorImport(campaignId, uploadData.job_id);
				return result;
			}

			// Contact import (default or prospect donor import)
			if (isProspectImport) {
				// Inject donor role metadata so imported contacts get roles=["donor"]
				mappings["__default_roles"] = JSON.stringify(["donor"]);
				mappings["__source"] = "donor_import";
			}

			const result = await apiService.confirmMapping(
				campaignId,
				uploadData.job_id,
				mappings,
			);
			if (!result.valid) {
				throw new Error(result.errors.join("; "));
			}
			await apiService.startImport(campaignId, uploadData.job_id);
			return result;
		},
		onSuccess: () => {
			onModeDetected?.(!!useDonorPipeline);
			onConfirm(uploadData.job_id);
		},
		onError: (err: Error) => setErrors([err.message]),
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center justify-between">
					<span>
						Column Mapping - {uploadData.filename} ({uploadData.total_rows}{" "}
						rows)
					</span>
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				{isProspectImport && (
					<div className="flex items-center gap-2 rounded-lg border border-blue-500/50 bg-blue-50 px-4 py-3 text-sm dark:bg-blue-900/10">
						<Users className="size-4 shrink-0 text-blue-600" />
						<span>
							No donation amounts detected — importing as{" "}
							<strong>prospect donors</strong> (contacts tagged with the donor
							role).
						</span>
					</div>
				)}

				<p className="text-sm text-muted-foreground">
					Review the auto-detected column mappings below. You can override any
					mapping using the dropdown.
				</p>

				<Table>
					<TableHeader>
						<TableRow>
							<TableHead className="pl-6">CSV Column</TableHead>
							<TableHead>Maps To</TableHead>
							<TableHead>Confidence</TableHead>
							<TableHead>Sample Values</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{columns.map((col, idx) => (
							<TableRow key={col.csv_header}>
								<TableCell className="pl-6 font-medium">
									{col.csv_header}
								</TableCell>
								<TableCell>
									<Select
										value={col.mapped_to ?? "__skip"}
										onValueChange={(v) => updateMapping(idx, v)}
									>
										<SelectTrigger className="w-48">
											<SelectValue placeholder="Skip" />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="__skip">Skip</SelectItem>
											{fields.map((f) => (
												<SelectItem key={f.value} value={f.value}>
													{f.label}
													{requiredFields.includes(f.value) ? " *" : ""}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</TableCell>
								<TableCell>{confidenceBadge(col.confidence)}</TableCell>
								<TableCell className="max-w-[200px] truncate text-xs text-muted-foreground">
									{col.sample_values.slice(0, 3).join(", ") || "---"}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>

				{missingRequired.length > 0 && (
					<div className="flex items-center gap-2 rounded-lg border border-yellow-500/50 bg-yellow-50 px-4 py-3 text-sm dark:bg-yellow-900/10">
						<AlertCircle className="size-4 shrink-0 text-yellow-600" />
						<span>
							Required fields not mapped:{" "}
							<strong>
								{missingRequired
									.map((f) => fields.find((cf) => cf.value === f)?.label ?? f)
									.join(", ")}
							</strong>
						</span>
					</div>
				)}

				{errors.length > 0 && (
					<div className="flex items-center gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
						<AlertCircle className="size-4 shrink-0" />
						{errors.join("; ")}
					</div>
				)}

				<div className="flex justify-end">
					<Button
						disabled={missingRequired.length > 0 || confirmMutation.isPending}
						onClick={() => confirmMutation.mutate()}
					>
						{confirmMutation.isPending ? (
							<Loader2 className="mr-2 size-4 animate-spin" />
						) : (
							<ChevronRight className="mr-2 size-4" />
						)}
						Confirm & Start Import
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Progress Step
// ---------------------------------------------------------------------------

function ProgressStep({
	campaignId,
	jobId,
	onComplete,
	isDonorImport,
}: {
	campaignId: number;
	jobId: number;
	onComplete: (job: CSVImportJob) => void;
	isDonorImport?: boolean;
}) {
	const session = useSession();
	const hasVoterFileAccess =
		session.campaign?.voter_file_enrichment_access ?? false;

	const { data: job } = useQuery({
		queryKey: ["import-job", campaignId, jobId],
		queryFn: () =>
			isDonorImport
				? apiService.getDonorImportJobStatus(campaignId, jobId)
				: apiService.getImportJobStatus(campaignId, jobId),
		refetchInterval: (query) => {
			const status = query.state.data?.status;
			if (status === "complete" || status === "failed") return false;
			return 2000;
		},
	});

	// Auto-transition to results when complete
	if (job && (job.status === "complete" || job.status === "failed")) {
		// Use setTimeout to avoid setting state during render
		setTimeout(() => onComplete(job), 0);
	}

	const totalRows = job?.total_rows ?? 0;
	const processedRows = (job?.rows_imported ?? 0) + (job?.rows_errored ?? 0);
	const progressPercent = totalRows > 0 ? (processedRows / totalRows) * 100 : 0;

	const statusLabel = (() => {
		switch (job?.status) {
			case "importing":
				return "Importing contacts...";
			case "enriching":
				return hasVoterFileAccess
					? "Matching voter records..."
					: "Finalizing...";
			case "complete":
				return "Complete!";
			case "failed":
				return "Import failed";
			default:
				return "Processing...";
		}
	})();

	return (
		<Card>
			<CardHeader>
				<CardTitle>Import Progress</CardTitle>
			</CardHeader>
			<CardContent className="space-y-6">
				<div className="space-y-2">
					<div className="flex items-center justify-between text-sm">
						<span className="font-medium">{statusLabel}</span>
						<span className="tabular-nums text-muted-foreground">
							{processedRows} / {totalRows} rows
						</span>
					</div>
					<Progress value={progressPercent} className="h-3" />
				</div>

				<div
					className={`grid grid-cols-2 gap-4 ${hasVoterFileAccess ? "sm:grid-cols-4" : "sm:grid-cols-2"}`}
				>
					<div className="rounded-lg border p-3 text-center">
						<p className="text-2xl font-bold tabular-nums">
							{job?.rows_imported ?? 0}
						</p>
						<p className="text-xs text-muted-foreground">Imported</p>
					</div>
					{hasVoterFileAccess && (
						<div className="rounded-lg border p-3 text-center">
							<p className="text-2xl font-bold tabular-nums text-green-600">
								{job?.rows_matched ?? 0}
							</p>
							<p className="text-xs text-muted-foreground">Matched</p>
						</div>
					)}
					{hasVoterFileAccess && (
						<div className="rounded-lg border p-3 text-center">
							<p className="text-2xl font-bold tabular-nums text-yellow-600">
								{job?.rows_not_found ?? 0}
							</p>
							<p className="text-xs text-muted-foreground">Not Found</p>
						</div>
					)}
					<div className="rounded-lg border p-3 text-center">
						<p className="text-2xl font-bold tabular-nums text-red-600">
							{job?.rows_errored ?? 0}
						</p>
						<p className="text-xs text-muted-foreground">Errors</p>
					</div>
				</div>

				{job?.status === "failed" && job.error_message && (
					<div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/5 px-4 py-3 text-sm text-destructive">
						<AlertCircle className="mt-0.5 size-4 shrink-0" />
						<p>{job.error_message}</p>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Results Step
// ---------------------------------------------------------------------------

function ResultsStep({
	job,
	campaignId,
	isDonorImport,
}: {
	job: CSVImportJob;
	campaignId: number;
	isDonorImport?: boolean;
}) {
	const navigate = useNavigate();
	const session = useSession();
	const hasVoterFileAccess =
		session.campaign?.voter_file_enrichment_access ?? false;

	const matchRate =
		job.rows_imported > 0
			? Math.round((job.rows_matched / job.rows_imported) * 100)
			: 0;

	const reportMutation = useMutation({
		mutationFn: () => apiService.submitImportReport(campaignId, job.id),
	});

	return (
		<Card>
			<CardHeader>
				<CardTitle className="flex items-center gap-2">
					{job.status === "complete" ? (
						<CheckCircle2 className="size-5 text-green-600" />
					) : (
						<X className="size-5 text-destructive" />
					)}
					{job.status === "complete" ? "Import Complete" : "Import Failed"}
				</CardTitle>
			</CardHeader>
			<CardContent className="space-y-6">
				{/* Summary */}
				{hasVoterFileAccess ? (
					<div className="rounded-lg border bg-muted/30 p-6 text-center">
						<p className="text-3xl font-bold">
							{job.rows_matched}{" "}
							<span className="text-lg font-normal text-muted-foreground">
								of {job.rows_imported} matched to voter records
							</span>
						</p>
						{job.rows_imported > 0 && (
							<div className="mt-2 flex items-center justify-center gap-2">
								<Badge
									className={
										matchRate >= 70
											? "bg-green-100 text-green-800"
											: matchRate >= 50
												? "bg-yellow-100 text-yellow-800"
												: "bg-red-100 text-red-800"
									}
								>
									{matchRate}% match rate
								</Badge>
							</div>
						)}
					</div>
				) : (
					<div className="rounded-lg border bg-muted/30 p-6 text-center">
						<p className="text-3xl font-bold">
							{job.rows_imported}{" "}
							<span className="text-lg font-normal text-muted-foreground">
								contacts imported
							</span>
						</p>
					</div>
				)}

				{/* Breakdown */}
				<div
					className={`grid grid-cols-2 gap-4 ${hasVoterFileAccess ? "sm:grid-cols-5" : "sm:grid-cols-3"}`}
				>
					<div className="rounded-lg border p-3 text-center">
						<p className="text-xl font-bold tabular-nums">{job.total_rows}</p>
						<p className="text-xs text-muted-foreground">Total Rows</p>
					</div>
					<div className="rounded-lg border p-3 text-center">
						<p className="text-xl font-bold tabular-nums">
							{job.rows_imported}
						</p>
						<p className="text-xs text-muted-foreground">Imported</p>
					</div>
					{hasVoterFileAccess && (
						<>
							<div className="rounded-lg border p-3 text-center">
								<p className="text-xl font-bold tabular-nums text-green-600">
									{job.rows_matched}
								</p>
								<p className="text-xs text-muted-foreground">Matched</p>
							</div>
							<div className="rounded-lg border p-3 text-center">
								<p className="text-xl font-bold tabular-nums text-yellow-600">
									{job.rows_not_found}
								</p>
								<p className="text-xs text-muted-foreground">Unmatched</p>
							</div>
						</>
					)}
					<div className="rounded-lg border p-3 text-center">
						<p className="text-xl font-bold tabular-nums text-red-600">
							{job.rows_errored}
						</p>
						<p className="text-xs text-muted-foreground">Errors</p>
					</div>
				</div>

				{/* Deduplication results */}
				{job.rows_deduplicated > 0 && (
					<div className="rounded-lg border bg-blue-50 p-4 dark:bg-blue-950/20">
						<p className="text-sm font-medium">
							{job.rows_deduplicated} duplicate
							{job.rows_deduplicated !== 1 ? "s" : ""} merged with existing
							contacts
						</p>
						{job.dedup_details && (
							<p className="mt-1 text-xs text-muted-foreground">
								{job.dedup_details.email > 0 &&
									`${job.dedup_details.email} matched by email`}
								{job.dedup_details.email > 0 &&
									job.dedup_details.phone > 0 &&
									", "}
								{job.dedup_details.phone > 0 &&
									`${job.dedup_details.phone} matched by phone`}
							</p>
						)}
					</div>
				)}

				{/* Error details */}
				{job.error_message && (
					<div className="space-y-2">
						<p className="text-sm font-medium">Error Details:</p>
						<pre className="max-h-32 overflow-auto rounded-lg bg-muted p-3 text-xs">
							{job.error_message}
						</pre>
						<Button
							variant="outline"
							size="sm"
							onClick={() => reportMutation.mutate()}
							disabled={reportMutation.isPending || reportMutation.isSuccess}
						>
							{reportMutation.isSuccess
								? "Report Sent"
								: "Send Report to Support"}
						</Button>
					</div>
				)}

				{/* Actions */}
				<div className="flex items-center justify-between">
					<Button
						variant="outline"
						onClick={() =>
							navigate({
								to: "/contacts/import",
								search: { from: isDonorImport ? "donors" : undefined },
							})
						}
					>
						Import Another
					</Button>
					<Button
						onClick={() =>
							isDonorImport
								? navigate({ to: "/fundraising", search: { tab: "donors" } })
								: navigate({ to: "/contacts", search: { tab: "your-voters" } })
						}
					>
						<Check className="mr-2 size-4" />
						{isDonorImport ? "View Donors" : "View Contacts"}
					</Button>
				</div>
			</CardContent>
		</Card>
	);
}

// ---------------------------------------------------------------------------
// Main Wizard
// ---------------------------------------------------------------------------

function CSVImportWizardPage() {
	const session = useSession();
	const campaignId = session.campaign?.id;
	const navigate = useNavigate();
	const { isReadWrite } = useRole();
	const { from } = Route.useSearch();
	const isDonorImport = from === "donors";

	const [step, setStep] = useState<WizardStep>("upload");
	const [uploadData, setUploadData] = useState<CSVUploadResponse | null>(null);
	const [jobId, setJobId] = useState<number | null>(null);
	const [completedJob, setCompletedJob] = useState<CSVImportJob | null>(null);
	// Tracks whether the donor import uses the full donation pipeline (amounts)
	// or the contact pipeline (prospect donors). Set after mapping step.
	const [usesDonorPipeline, setUsesDonorPipeline] = useState(false);

	if (!isReadWrite) {
		return (
			<div className="p-6">
				<PermissionDeniedPage
					requiredRole="Read/Write"
					message="You need Read/Write or Admin permissions to import contacts."
				/>
			</div>
		);
	}

	if (!campaignId) {
		return (
			<div className="min-h-screen bg-muted/30 p-6">
				<div className="mx-auto max-w-4xl">
					<p className="text-muted-foreground">
						No campaign found. Please complete onboarding first.
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-muted/30 p-6">
			<div className="mx-auto max-w-4xl space-y-6">
				{/* Header */}
				<div className="flex items-center gap-3">
					<Button
						variant="ghost"
						size="icon-sm"
						onClick={() =>
							isDonorImport
								? navigate({ to: "/fundraising", search: { tab: "donors" } })
								: navigate({ to: "/contacts", search: { tab: "your-voters" } })
						}
					>
						<ArrowLeft className="size-4" />
					</Button>
					<h1 className="text-2xl font-semibold">
						{isDonorImport ? "Import Donors" : "Import Contacts"}
					</h1>
				</div>

				{/* Step indicator */}
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<span
						className={step === "upload" ? "font-medium text-foreground" : ""}
					>
						1. Upload
					</span>
					<ChevronRight className="size-3" />
					<span
						className={step === "mapping" ? "font-medium text-foreground" : ""}
					>
						2. Map Columns
					</span>
					<ChevronRight className="size-3" />
					<span
						className={step === "progress" ? "font-medium text-foreground" : ""}
					>
						3. Processing
					</span>
					<ChevronRight className="size-3" />
					<span
						className={step === "results" ? "font-medium text-foreground" : ""}
					>
						4. Results
					</span>
				</div>

				{/* Steps */}
				{step === "upload" && (
					<UploadStep
						campaignId={campaignId}
						isDonorImport={isDonorImport}
						onUploadComplete={(data) => {
							setUploadData(data);
							setStep("mapping");
						}}
					/>
				)}

				{step === "mapping" && uploadData && (
					<MappingStep
						uploadData={uploadData}
						campaignId={campaignId}
						isDonorImport={isDonorImport}
						onModeDetected={(hasDonationAmounts) =>
							setUsesDonorPipeline(hasDonationAmounts)
						}
						onConfirm={(id) => {
							setJobId(id);
							setStep("progress");
						}}
					/>
				)}

				{step === "progress" && jobId && (
					<ProgressStep
						campaignId={campaignId}
						jobId={jobId}
						isDonorImport={usesDonorPipeline}
						onComplete={(job) => {
							trackCsvImported(job.rows_imported);
							setCompletedJob(job);
							setStep("results");
						}}
					/>
				)}

				{step === "results" && completedJob && (
					<ResultsStep
						job={completedJob}
						campaignId={campaignId}
						isDonorImport={isDonorImport}
					/>
				)}
			</div>
		</div>
	);
}
